import { sendToSentry } from "@/utils/sentry";

import { CustomRequestInit } from "./types";

// Constants for retry mechanism
const MAX_RETRIES = 3;
const INITIAL_BACKOFF_MS = 1000; // 1 second
const JITTER_FACTOR = 0.3; // Add up to 30% of backoff time as jitter

export const isClientError = (status: number): boolean => {
  return status >= 400 && status < 500;
};

const IDEMPOTENT_METHODS = new Set(["GET", "HEAD", "PUT", "DELETE", "OPTIONS"]);

// A POST that timed out may still have run on the server: retrying it can create a second case or ticket.
export const isRetryableRequest = ({ method, retry }: Pick<CustomRequestInit, "method" | "retry">): boolean =>
  retry === true || IDEMPOTENT_METHODS.has((method ?? "GET").toUpperCase());

export async function fetchWithRetry(url: string, requestOptions: CustomRequestInit): Promise<Response> {
  const { retry, ...options } = requestOptions;
  const maxAttempts = isRetryableRequest({ method: options.method, retry }) ? MAX_RETRIES : 1;
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await fetch(url, options);

      // If response is OK (2xx) or a client error (4xx), return it immediately.
      // Client errors (4xx) are handled by the caller
      if (response.ok) {
        if (attempt > 1) {
          console.info(`Fetch retry successful for ${url} on attempt ${attempt}/${maxAttempts}`);
        }
        return response;
      }

      if (isClientError(response.status)) {
        return response;
      }

      // At this point, response is not OK and not a 4xx client error.
      if (response.status >= 500 && response.status <= 599) {
        const errorMessage = `Server error: ${response.status} on attempt ${attempt}/${maxAttempts} for ${url}`;
        lastError = new Error(errorMessage);

        if (attempt === maxAttempts) {
          sendToSentry(
            new Error(
              `Failed after ${maxAttempts} attempts (5xx): ${response.status} for ${url}. Last attempt error: ${errorMessage}`,
            ),
            {
              url,
              method: options.method,
              options,
              response,
              additionalContext: {
                component: "fetchWithRetry",
                phase: "retry_failure_5xx_exhausted",
                attempt,
                maxRetries: maxAttempts,
                status: response.status,
              },
            },
          );
          console.warn(
            `Final attempt (${attempt}/${maxAttempts}) failed for ${url} with status ${response.status}. Returning this response.`,
          );
          return response; // Return the last 5xx response if all retries exhausted
        }

        console.warn(`Attempt ${attempt}/${maxAttempts} for ${url} failed with status ${response.status}. Retrying...`);
      } else {
        // Non-5xx error that is also not response.ok and not isClientError
        // We don't retry these by default.
        console.warn(
          `Attempt ${attempt}/${maxAttempts} for ${url} resulted in non-retryable status ${response.status}. Returning this response.`,
        );
        return response;
      }
    } catch (error) {
      // This block catches network errors or if fetch itself fails
      lastError = error instanceof Error ? error : new Error(String(error));

      // Don't retry if the request was aborted
      if (lastError instanceof DOMException && lastError.name === "AbortError") {
        throw lastError;
      }

      if (attempt === maxAttempts) {
        sendToSentry(
          new Error(
            `Failed after ${maxAttempts} attempts (network error) for ${url}. Last error: ${lastError.message}`,
          ),
          {
            url,
            method: options.method,
            options,
            // No response object available for network errors typically
            additionalContext: {
              component: "fetchWithRetry",
              phase: "retry_failure_network_exhausted",
              attempt,
              maxRetries: maxAttempts,
              originalError: lastError.toString(), // Include original error string
            },
          },
        );
        console.error(`Final attempt (${attempt}/${maxAttempts}) for ${url} failed with network error.`, lastError);
        throw lastError; // Re-throw network error if all retries exhausted
      }

      console.warn(
        `Attempt ${attempt}/${maxAttempts} for ${url} failed with network error: ${lastError.message}. Retrying...`,
      );
    }

    // If we reach here, a retry is pending (due to 5xx or network error, and not max attempts)
    if (attempt < maxAttempts) {
      const backoff = INITIAL_BACKOFF_MS * Math.pow(2, attempt - 1);
      const jitterValue = backoff * JITTER_FACTOR * Math.random();
      const delay = backoff + jitterValue;
      // console.log(`Waiting ${delay.toFixed(0)}ms before next attempt for ${url}`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  if (lastError) {
    throw lastError;
  }
  throw new Error(`fetchWithRetry exhausted attempts for ${url} without a conclusive result.`);
}
